import type { GraphQLAuditItem, GraphQLAuditDecision } from '@internal-audit/domain/graphql-types';
import { useCallback, useState } from 'react';

import { useDeleteAuditDecision } from '../../../hooks/useDeleteAuditDecision';

interface UseDeleteDecisionDialogProps {
  selectedItem: GraphQLAuditItem | null;
  currentUserDecision?: GraphQLAuditDecision;
  onClose: () => void;
}

/**
 * Custom hook to manage delete decision dialog state and operations
 * Extracted to reduce component complexity and improve maintainability
 */
export function useDeleteDecisionDialog({ selectedItem, currentUserDecision, onClose }: UseDeleteDecisionDialogProps) {
  const { deleteAuditDecision, loading: deleteLoading, errors: deleteErrors } = useDeleteAuditDecision();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteErrorToast, setDeleteErrorToast] = useState<string | null>(null);

  // Handle delete decision
  const handleDeleteDecision = useCallback(async () => {
    if (!selectedItem || !currentUserDecision) return;

    try {
      const result = await deleteAuditDecision({
        input: {
          decisionId: currentUserDecision.id,
          auditItemId: selectedItem.id,
          auditSessionId: selectedItem.auditSessionId,
        },
      });

      if (result.success) {
        // Close the dialog and panel on successful deletion
        setIsDeleteDialogOpen(false);
        onClose();
      } else {
        // Show error toast if deletion failed
        const errorMessage = result.errors?.[0]?.message || 'Failed to delete decision';

        setDeleteErrorToast(errorMessage);
        setIsDeleteDialogOpen(false);
      }
    } catch (error) {
      // Handle unexpected errors
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

      setDeleteErrorToast(errorMessage);
      setIsDeleteDialogOpen(false);
    }
  }, [selectedItem, currentUserDecision, deleteAuditDecision, onClose]);

  // Open delete dialog
  const openDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  // Close delete dialog
  const closeDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
  }, []);

  // Clear error toast
  const clearErrorToast = useCallback(() => {
    setDeleteErrorToast(null);
  }, []);

  return {
    isDeleteDialogOpen,
    deleteLoading,
    deleteErrors,
    deleteErrorToast,
    handleDeleteDecision,
    openDeleteDialog,
    closeDeleteDialog,
    clearErrorToast,
  };
}
